/**
 * Transition registry — route-pair resolution with dynamic imports.
 *
 * Maps source:destination namespace pairs to transition modules.
 * Resolves in priority order: exact → *:to → from:* → *:*
 * Transition modules are loaded on demand to keep the critical path small.
 *
 * @see specs/002-swup-view-transitions/contracts/router-api.md
 */

/**
 * Route-pair → transition module path map.
 *
 * Keys use "from:to" format where namespaces come from body[data-namespace].
 * Wildcard (*) matches any namespace. Resolution order: exact → *:to → from:* → *:*
 *
 * This is a PROJECT OVERRIDE POINT — a fork adds pairs for its own namespaces.
 *
 * ── KEYED ON GNG'S NAMESPACES, 28 AUG 2026 ──────────────────────────────────
 *
 * GNG serves four: `home`, `default` (every page entry, both indexes included),
 * `case_studies` and `people` (the two detail collections). It shipped keyed on
 * `home:_docs` — the zero-content kit's only navigable pair — which GNG's chrome
 * never links to, so the ONLY reachable path was the `*:*` default.
 *
 * That is the third instance of the same failure in this file's history. It
 * first shipped 'work:projects/show' and 'work:*', namespaces from a demo site
 * whose content had been removed; those were replaced with the kit's own, which
 * this fork then stopped serving. **A pair that can never match is
 * indistinguishable from a broken resolver** — precisely the state the
 * `visit.dataset` bug produced, and therefore invisible to the guard meant to
 * catch it. Re-check these keys against `body[data-namespace]` whenever the
 * routes change.
 *
 * WHY THE DETAIL PAGES ARE LEFT ON THE DEFAULT. A card click into a case study
 * runs the reversible card morph (transitions/card-morph.js), which names the
 * clicked card and the incoming hero so the browser animates one into the other.
 * A page-level transition that also animated those elements would fight it, so
 * the crossfade is the right partner: it handles everything the morph does not.
 */
const transitionMap = {
    // Into a listing. Its cards carry `[data-stagger]`, so they arrive in
    // sequence rather than all at once. Safe as a broad key: stagger falls back
    // to crossfade when it finds no stagger items, so contact and services take
    // the same entry and are unaffected by it.
    'home:default':       'stagger',
    // Back to home, where reveal animates the headings as well as the cards.
    '*:home':             'reveal',
    '*:*':                'crossfade',
};

/**
 * Module cache — avoids re-importing the same transition module.
 */
const moduleCache = new Map();

/**
 * The transition modules, named one by one.
 *
 * ── WHY NOT `import(`../transitions/${name}.js`)` ───────────────────────────
 *
 * A dynamic import built from a template literal is a GLOB to the bundler: it
 * cannot know which name will arrive, so every file in `transitions/` becomes a
 * candidate chunk. That directory holds four modules that are not transitions at
 * all — `base.js` (the shared class), `card-morph.js`, `query-cascade.js` and
 * `scroll-reveal.js` — and the last three are ALSO imported statically, by
 * swup.js and app.js, because they are init functions the router needs at boot.
 *
 * A module that is both statically imported and a dynamic-import candidate
 * cannot be split out, which is exactly what the build said three times:
 *
 *   [INEFFECTIVE_DYNAMIC_IMPORT] card-morph.js is dynamically imported by
 *   transitions.js but also statically imported by swup.js, dynamic import will
 *   not move module into another chunk.
 *
 * Naming each transition explicitly makes every entry a statically analysable
 * import, so the five real transitions split into their own chunks and the four
 * non-transitions stop being candidates. The warnings go with it.
 *
 * A FORK ADDING A TRANSITION ADDS IT HERE AS WELL AS TO `transitionMap`. That is
 * one more line than the glob needed, and it is the line that keeps the bundle
 * honest — the map above is the routing table, this is the manifest.
 *
 * @type {Record<string, () => Promise<{out: Function, in: Function}>>}
 */
const TRANSITION_MODULES = {
    crossfade: () => import('../transitions/crossfade.js'),
    stagger: () => import('../transitions/stagger.js'),
    reveal: () => import('../transitions/reveal.js'),
    slide: () => import('../transitions/slide.js'),
    'flip-expand': () => import('../transitions/flip-expand.js'),
};

/**
 * Dynamically import a transition module by name.
 *
 * @param {string} name - Transition module name (e.g. 'crossfade', 'slide')
 * @returns {Promise<{out: Function, in: Function}>}
 */
function loadTransition(name) {
    if (moduleCache.has(name)) {
        return moduleCache.get(name);
    }

    // An unknown name is a registry entry naming a module nobody added here.
    // Falling back to crossfade keeps the navigation working rather than
    // rejecting a promise the caller does not expect to reject.
    const load = TRANSITION_MODULES[name] ?? TRANSITION_MODULES.crossfade;
    const promise = load();
    moduleCache.set(name, promise);
    return promise;
}

/**
 * Resolve the transition name for a visit based on route-pair matching.
 *
 * Resolution order:
 * 1. Exact match: "from:to"
 * 2. Wildcard destination: "*:to"
 * 3. Wildcard source: "from:*"
 * 4. Default: "*:*" (always crossfade)
 *
 * @param {string} from - Source namespace
 * @param {string} to - Destination namespace
 * @returns {string} Transition name
 */
function resolveTransitionName(from, to) {
    const exact = `${from}:${to}`;
    if (transitionMap[exact]) return transitionMap[exact];

    const wildcardTo = `*:${to}`;
    if (transitionMap[wildcardTo]) return transitionMap[wildcardTo];

    const wildcardFrom = `${from}:*`;
    if (transitionMap[wildcardFrom]) return transitionMap[wildcardFrom];

    return transitionMap['*:*'];
}

/**
 * Resolve the transition for a Swup visit.
 *
 * Namespaces come from `body[data-namespace]`, NOT from the visit object:
 * Swup v4's VisitFrom/VisitTo are `{ url, hash }` plus (post-fetch) a parsed
 * `to.document` — there is no `visit.from.dataset` / `visit.to.dataset`, so the
 * previous lookup always read `undefined` and every visit fell through to the
 * `*:*` crossfade default. FROM is the live page's body (unchanged until the
 * swap); TO is the fetched page's body. `to.document` is only populated after
 * the fetch resolves, so callers must invoke this from a post-fetch hook
 * (see router/swup.js — `before('content:replace')`).
 *
 * Sets visit.animation.name to the resolved transition name, then returns the
 * dynamically imported transition module.
 *
 * @param {Object} visit - Swup visit object
 * @returns {Promise<{out: Function, in: Function}>}
 */
export function resolveTransition(visit) {
    const from = document.body?.dataset?.namespace || '*';
    const to = visit.to?.document?.body?.dataset?.namespace || '*';

    const name = resolveTransitionName(from, to);
    visit.animation.name = name;

    return loadTransition(name);
}
